import { format, endOfMonth } from 'date-fns'
import {
  Activity,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Zap,
  DollarSign,
  BarChart3,
  Clock,
  Target,
  Plus
} from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'

interface AgentUsageDialogProps {
  isOpen: boolean
  onClose: () => void
  agentId: string
  agentName: string
  projectId: string
}

interface UsageData {
  currentMonth: {
    actionsUsed: number
    actionsLimit: number
    costEstimate: number
  }
  previousMonth: {
    actionsUsed: number
    costEstimate: number
  }
  yearToDate: {
    actionsUsed: number
    costEstimate: number
  }
  dailyBreakdown: {
    date: string
    actions: number
    cost: number
  }[]
  actionTypes: {
    type: string
    count: number
    cost: number
    averageExecutionTime: number
  }[]
  subscription: {
    tier: string
    monthlyLimit: number
    costPerAction: number
    nextBillingDate: string
  }
}

interface TopUpOption {
  actions: number
  cost: number
  popular?: boolean
}

const AgentUsageDialog: React.FC<AgentUsageDialogProps> = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  projectId
}) => {
  const [usage, setUsage] = useState<UsageData | null>(null)
  const [loading, setLoading] = useState(false)
  const [topUpOptions] = useState<TopUpOption[]>([
    { actions: 1000, cost: 9.99 },
    { actions: 5000, cost: 39.99, popular: true },
    { actions: 10000, cost: 69.99 },
    { actions: 25000, cost: 149.99 }
  ])

  // Calculate usage percentage
  const usagePercentage = usage ? (usage.currentMonth.actionsUsed / usage.currentMonth.actionsLimit) * 100 : 0
  const remainingActions = usage ? usage.currentMonth.actionsLimit - usage.currentMonth.actionsUsed : 0

  // Get usage status
  const getUsageStatus = () => {
    if (usagePercentage >= 100) return { color: 'bg-red-500', text: 'Limit Exceeded', textColor: 'text-red-600' }
    if (usagePercentage >= 90) return { color: 'bg-orange-500', text: 'Almost Full', textColor: 'text-orange-600' }
    if (usagePercentage >= 75) return { color: 'bg-yellow-500', text: 'High Usage', textColor: 'text-yellow-600' }
    return { color: 'bg-green-500', text: 'Normal', textColor: 'text-green-600' }
  }

  // Fetch usage data
  const fetchUsageData = async () => {
    if (!agentId || !projectId) return
    
    setLoading(true)
    try {
      const response = await fetch(`/api/projects/${projectId}/agents/${agentId}/usage`)
      if (!response.ok) {
        throw new Error(`Failed to fetch usage data: ${response.status}`)
      }
      const usageData = await response.json()
      setUsage(usageData)
    } catch (error) {
      console.error('Failed to fetch agent usage:', error)
      
      // Mock data for development
      const mockUsage: UsageData = {
        currentMonth: {
          actionsUsed: 2847,
          actionsLimit: 5000,
          costEstimate: 28.47
        },
        previousMonth: {
          actionsUsed: 4231,
          costEstimate: 42.31
        },
        yearToDate: {
          actionsUsed: 34892,
          costEstimate: 348.92
        },
        dailyBreakdown: Array.from({ length: 30 }, (_, i) => ({
          date: format(new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          actions: Math.floor(Math.random() * 200) + 50,
          cost: Math.random() * 2 + 0.5
        })),
        actionTypes: [
          { type: 'document_process', count: 1245, cost: 12.45, averageExecutionTime: 2.3 },
          { type: 'research_task', count: 856, cost: 8.56, averageExecutionTime: 4.7 },
          { type: 'analysis', count: 423, cost: 4.23, averageExecutionTime: 1.8 },
          { type: 'observation', count: 323, cost: 3.23, averageExecutionTime: 0.5 }
        ],
        subscription: {
          tier: 'Professional',
          monthlyLimit: 5000,
          costPerAction: 0.01,
          nextBillingDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
        }
      }
      setUsage(mockUsage)
    } finally {
      setLoading(false)
    }
  }

  // Handle action top-up
  const handleTopUp = async (option: TopUpOption) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/agents/${agentId}/top-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          actions: option.actions,
          cost: option.cost
        })
      })

      if (!response.ok) {
        throw new Error('Failed to process top-up')
      }

      toast.success(`Successfully added ${option.actions.toLocaleString()} actions to your account!`)
      fetchUsageData() // Refresh data
    } catch (error) {
      console.error('Top-up failed:', error)
      toast.error('Failed to process top-up. Please try again.')
    }
  }

  // Load usage data when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchUsageData()
    }
  }, [isOpen, agentId, projectId])

  const status = getUsageStatus()

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <BarChart3 className="w-5 h-5" />
            <span>Usage Statistics - {agentName}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : usage ? (
          <div className="space-y-6">
            {/* Usage Alert */}
            {usagePercentage >= 75 && (
              <Alert className={usagePercentage >= 90 ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className={status.textColor}>
                  {usagePercentage >= 100 ? (
                    <>You have exceeded your monthly limit. Consider upgrading your plan or purchasing additional actions.</>
                  ) : usagePercentage >= 90 ? (
                    <>You are approaching your monthly limit ({remainingActions.toLocaleString()} actions remaining).</>
                  ) : (
                    <>You have used {usagePercentage.toFixed(1)}% of your monthly actions.</>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Current Month Overview */}
            <Card>
              <CardHeader hasCardAction={false} className="">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Activity className="w-5 h-5" />
                    <span>Current Month Usage</span>
                  </span>
                  <Badge variant={usagePercentage >= 100 ? 'destructive' : usagePercentage >= 75 ? 'secondary' : 'default'}>
                    {status.text}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Actions Used</span>
                      <span className="font-medium">
                        {usage.currentMonth.actionsUsed.toLocaleString()} / {usage.currentMonth.actionsLimit.toLocaleString()}
                      </span>
                    </div>
                    <Progress value={Math.min(usagePercentage, 100)} className="h-3" />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {remainingActions.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Remaining Actions</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        ${usage.currentMonth.costEstimate.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">Current Cost</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {usage.subscription.tier}
                      </div>
                      <div className="text-sm text-gray-500">Plan</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader hasCardAction={false} className="pb-2">
                  <CardTitle className="text-sm flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Previous Month</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold">{usage.previousMonth.actionsUsed.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">${usage.previousMonth.costEstimate.toFixed(2)} cost</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader hasCardAction={false} className="pb-2">
                  <CardTitle className="text-sm flex items-center space-x-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>Year to Date</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold">{usage.yearToDate.actionsUsed.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">${usage.yearToDate.costEstimate.toFixed(2)} cost</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader hasCardAction={false} className="pb-2">
                  <CardTitle className="text-sm flex items-center space-x-1">
                    <DollarSign className="w-4 h-4" />
                    <span>Cost Per Action</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold">${usage.subscription.costPerAction.toFixed(3)}</div>
                  <div className="text-sm text-gray-500">Current rate</div>
                </CardContent>
              </Card>
            </div>

            {/* Action Types Breakdown */}
            <Card>
              <CardHeader hasCardAction={false} className="">
                <CardTitle className="flex items-center space-x-2">
                  <Target className="w-5 h-5" />
                  <span>Action Types Breakdown</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {usage.actionTypes.map((actionType, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">{actionType.type.replace('_', ' ').toUpperCase()}</div>
                          <div className="text-sm text-gray-500">
                            Avg execution: {actionType.averageExecutionTime}s
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{actionType.count.toLocaleString()}</div>
                          <div className="text-sm text-gray-500">${actionType.cost.toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Top-up Options */}
            {usagePercentage >= 75 && (
              <Card>
                <CardHeader hasCardAction={false} className="">
                  <CardTitle className="flex items-center space-x-2">
                    <Plus className="w-5 h-5" />
                    <span>Purchase Additional Actions</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {topUpOptions.map((option, index) => (
                      <div key={index} className={`relative p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer ${option.popular ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                        {option.popular && (
                          <Badge className="absolute -top-2 left-4 bg-blue-500">Most Popular</Badge>
                        )}
                        <div className="text-center">
                          <div className="text-2xl font-bold">{option.actions.toLocaleString()}</div>
                          <div className="text-sm text-gray-500 mb-2">actions</div>
                          <div className="text-xl font-bold text-green-600">${option.cost}</div>
                          <Button 
                            className="w-full mt-3" 
                            variant={option.popular ? 'default' : 'outline'}
                            onClick={() => handleTopUp(option)}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            Buy Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Actions are added instantly to your account
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Billing Information */}
            <Card>
              <CardHeader hasCardAction={false} className="">
                <CardTitle className="text-sm">Billing Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Current Plan:</span> {usage.subscription.tier}
                  </div>
                  <div>
                    <span className="font-medium">Monthly Limit:</span> {usage.subscription.monthlyLimit.toLocaleString()} actions
                  </div>
                  <div>
                    <span className="font-medium">Next Billing:</span> {format(new Date(usage.subscription.nextBillingDate), 'MMM dd, yyyy')}
                  </div>
                  <div>
                    <span className="font-medium">Rate:</span> ${usage.subscription.costPerAction} per action
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            Failed to load usage data
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AgentUsageDialog
